/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  CompoundEntityRef,
  Entity,
  RELATION_MEMBER_OF,
  UserEntity,
  getCompoundEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  InfoCard,
  InfoCardVariants,
  Link,
  Progress,
} from '@backstage/core-components';
import {
  Avatar,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  EntityRefLink,
  entityPresentationSnapshot,
  useEntity,
  useRelatedEntities,
} from '@backstage/plugin-catalog-react';
import React from 'react';

import Alert from '@material-ui/lab/Alert';
import EditIcon from '@material-ui/icons/Edit';
import EmailIcon from '@material-ui/icons/Email';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import { TeamsIcon } from '../../icons/TeamsIcon';
import { teamsChatUrl, teamsChatUserOf } from '../../TeamsChat';

/**
 * Where people keep their own details - name, photo, job title - which the
 * catalog takes over from Entra. Nothing about a user is edited in the
 * catalog itself.
 */
const IAM_PORTAL_URL = 'https://iam.netrtl.com/';

/** The photo, the name and, where Entra has one, the job title below it. */
const CardTitle = (props: {
  title: string;
  jobTitle?: string;
  pictureSrc?: string;
}) => (
  <Box display="flex" alignItems="center">
    <Avatar src={props.pictureSrc} alt={props.title} />
    <Box ml={1}>
      {props.title}
      {props.jobTitle && (
        <Typography variant="body2" color="textSecondary">
          {props.jobTitle}
        </Typography>
      )}
    </Box>
  </Box>
);

/**
 * The profile card of a user: how to reach them, and the Entra groups they
 * are in.
 *
 * The teams are left to the Organization card next to it, which shows every
 * department and team the user is an editor, a viewer or a contact of. The
 * Entra groups
 * are the `memberOf` groups that are no team - since the Entra memberships
 * are ingested, `memberOf` holds the teams as well.
 *
 * @public
 */
export const UserProfileCard = (props: { variant?: InfoCardVariants }) => {
  const { entity: user } = useEntity<UserEntity>();
  const {
    entities: groupEntities,
    loading: groupsLoading,
    error: groupsError,
  } = useRelatedEntities(user, {
    type: RELATION_MEMBER_OF,
    kind: 'Group',
  });

  if (!user) {
    return <Alert severity="error">User not found</Alert>;
  }

  // An unanswered lookup must not fall through to the row below: N/A there
  // reads as "in no Entra group", which the card must not say by accident.
  if (groupsError) {
    return <Alert severity="error">{groupsError.message}</Alert>;
  }

  const {
    metadata: { name },
    spec: { profile },
  } = user;

  const displayName = profile?.displayName ?? name;
  // Entra's job title, see the user transformer of the catalog module.
  const jobTitle = (profile as { title?: string } | undefined)?.title;
  const emailHref = profile?.email ? `mailto:${profile.email}` : '#';
  const teamsUser = teamsChatUserOf(user);

  const adGroups = refsOf(groupEntities?.filter(it => !isTeam(it)));

  return (
    <InfoCard
      title={
        <CardTitle
          title={displayName}
          jobTitle={jobTitle}
          pictureSrc={profile?.picture}
        />
      }
      variant={props.variant ?? 'fullHeight'}
      action={
        <Tooltip title="Edit in the IAM portal">
          <IconButton
            aria-label="Edit in the IAM portal"
            href={IAM_PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      }
    >
      <List>
        {teamsUser && (
          <ListItem>
            <ListItemIcon>
              <Tooltip title="Microsoft Teams">
                <TeamsIcon />
              </Tooltip>
            </ListItemIcon>
            <ListItemText
              primary={
                <Link to={teamsChatUrl({ users: [teamsUser] })}>
                  Chat with {displayName}
                </Link>
              }
              secondary="Microsoft Teams"
            />
          </ListItem>
        )}
        {profile?.email && (
          <ListItem>
            <ListItemIcon>
              <Tooltip title="Email">
                <EmailIcon />
              </Tooltip>
            </ListItemIcon>
            <ListItemText
              primary={<Link to={emailHref}>{profile.email}</Link>}
              secondary="Email"
            />
          </ListItem>
        )}
        <ListItem>
          <ListItemIcon>
            <Tooltip title="Entra Groups">
              <VpnKeyIcon />
            </Tooltip>
          </ListItemIcon>
          <ListItemText
            primary={
              <MembershipList loading={groupsLoading} entityRefs={adGroups} />
            }
            secondary="Entra Groups"
          />
        </ListItem>
      </List>
    </InfoCard>
  );
};

/**
 * The membership row, one group per line. A dozen Entra groups run together
 * with commas read as one long string of text; on their own lines they can be
 * scanned.
 *
 * Which group is a team is only known once the catalog has answered, so the
 * row shows nothing before that - an empty row would read as "in no group".
 */
function MembershipList(props: {
  loading: boolean;
  entityRefs: CompoundEntityRef[];
}) {
  if (props.loading) {
    return <Progress />;
  }
  if (props.entityRefs.length === 0) {
    return <>N/A</>;
  }
  return (
    // A span rather than a div: the row sits in the Typography of a
    // ListItemText, which is a span itself.
    <Box component="span" display="flex" flexDirection="column">
      {props.entityRefs.map(entityRef => (
        <EntityRefLink
          key={stringifyEntityRef(entityRef)}
          entityRef={entityRef}
          defaultKind="Group"
        />
      ))}
    </Box>
  );
}

function isTeam(entity: Entity): boolean {
  return entity.spec?.type === 'team';
}

/**
 * Sorted by the label the link is drawn with rather than by entity name, which
 * is not always what the row shows.
 */
function refsOf(entities: Entity[] | undefined): CompoundEntityRef[] {
  return (entities ?? [])
    .sort((a, b) =>
      entityPresentationSnapshot(a).primaryTitle.localeCompare(
        entityPresentationSnapshot(b).primaryTitle,
        'en-US',
      ),
    )
    .map(getCompoundEntityRef);
}
